import { config } from './config';
import type { FAQInput, FAQResult, FAQItem, FAQValidationError, GeneratorOptions } from './types/company';
import { stripHtmlTags, Result, ok, fail, isOk, safeString, isValidUrl, LRUCache } from './utils';
import { logger } from './utils/logger';

const faqCache = new LRUCache<string, FAQResult>({
    maxEntries: config.getConfig().cache.maxDurationEntries,
});

/**
 * Validates FAQ input data for schema generation.
 *
 * @param input - Unknown input to validate as FAQ data
 * @returns Result containing valid FAQInput or array of validation errors
 */
function validateFAQInput(input: unknown): Result<FAQInput, FAQValidationError[]> {
    const errors: FAQValidationError[] = [];

    if (!input || typeof input !== 'object') {
        return fail([{ field: 'title', message: 'FAQ input must be an object' }]);
    }

    const faq = input as Record<string, unknown>;

    if (!faq.title || typeof faq.title !== 'string') {
        errors.push({
            field: 'title',
            message: 'Title is required and must be a string',
            value: faq.title,
        });
    }

    if (!faq.questions || !Array.isArray(faq.questions)) {
        errors.push({
            field: 'questions',
            message: 'Questions array is required',
            value: faq.questions,
        });
    } else {
        faq.questions.forEach((question, index) => {
            if (!question || typeof question !== 'object') {
                errors.push({
                    field: 'questions',
                    message: `Question at index ${index} must be an object`,
                    value: question,
                    questionIndex: index,
                });
                return;
            }

            const q = question as Record<string, unknown>;

            if (!q.question || typeof q.question !== 'string') {
                errors.push({
                    field: 'question',
                    message: `Question text is required at index ${index}`,
                    value: q.question,
                    questionIndex: index,
                });
            }

            if (!q.answer || typeof q.answer !== 'string') {
                errors.push({
                    field: 'answer',
                    message: `Answer text is required at index ${index}`,
                    value: q.answer,
                    questionIndex: index,
                });
            }

            if (q.category !== undefined && typeof q.category !== 'string') {
                errors.push({
                    field: 'category',
                    message: `Category must be a string at index ${index}`,
                    value: q.category,
                    questionIndex: index,
                });
            }

            if (q.order !== undefined && typeof q.order !== 'number') {
                errors.push({
                    field: 'order',
                    message: `Order must be a number at index ${index}`,
                    value: q.order,
                    questionIndex: index,
                });
            }
        });

        if (faq.questions.length === 0) {
            errors.push({
                field: 'questions',
                message: 'At least one question is required',
                value: faq.questions,
            });
        }
    }

    if (faq.url && typeof faq.url === 'string') {
        if (!isValidUrl(faq.url)) {
            errors.push({
                field: 'url',
                message: 'Invalid URL format',
                value: faq.url,
            });
        }
    }

    if (faq.lastModified && typeof faq.lastModified === 'string') {
        const date = new Date(faq.lastModified);
        if (isNaN(date.getTime())) {
            errors.push({
                field: 'lastModified',
                message: 'Invalid date format for lastModified',
                value: faq.lastModified,
            });
        }
    }

    if (errors.length > 0) {
        return fail(errors);
    }

    return ok(faq as unknown as FAQInput);
}

/**
 * Builder class for constructing FAQ schema objects.
 * Provides a fluent interface for setting FAQ properties and questions.
 */
class FAQBuilder {
    private faq: Partial<FAQResult>;

    constructor(title: string) {
        this.faq = {
            '@type': 'FAQPage',
            name: stripHtmlTags(safeString(title)),
            mainEntity: [],
        };
    }

    /**
     * Sets the description for the FAQ page.
     *
     * @param description - The description text
     * @returns This builder instance for chaining
     */
    setDescription(description?: string): FAQBuilder {
        if (description) {
            this.faq.description = stripHtmlTags(safeString(description));
        }
        return this;
    }

    /**
     * Sets the URL and @id for the FAQ page.
     *
     * @param url - The URL of the FAQ page
     * @returns This builder instance for chaining
     */
    setUrl(url?: string): FAQBuilder {
        if (url) {
            this.faq.url = stripHtmlTags(safeString(url));
            this.faq['@id'] = url;
        }
        return this;
    }

    /**
     * Sets the last modified date for the FAQ page.
     *
     * @param date - The last modified date string
     * @returns This builder instance for chaining
     */
    setLastModified(date?: string): FAQBuilder {
        if (date) {
            const parsedDate = new Date(date);
            if (!isNaN(parsedDate.getTime())) {
                this.faq.dateModified = parsedDate.toISOString();
            }
        }
        return this;
    }

    /**
     * Sets the publisher organization for the FAQ page.
     *
     * @param publisher - The publisher organization name
     * @returns This builder instance for chaining
     */
    setPublisher(publisher?: string): FAQBuilder {
        if (publisher) {
            this.faq.publisher = {
                '@type': 'Organization',
                name: stripHtmlTags(safeString(publisher)),
            };
        }
        return this;
    }

    /**
     * Sets the language for the FAQ page.
     *
     * @param language - The language code (e.g., 'en', 'es')
     * @returns This builder instance for chaining
     */
    setLanguage(language?: string): FAQBuilder {
        if (language) {
            this.faq.inLanguage = stripHtmlTags(safeString(language));
        }
        return this;
    }

    /**
     * Adds a single question and answer to the FAQ.
     *
     * @param question - The question text
     * @param answer - The answer text
     * @param _category - Optional category (currently unused)
     * @returns This builder instance for chaining
     */
    addQuestion(question: string, answer: string, _category?: string): FAQBuilder {
        if (question && answer) {
            const questionEntity = {
                '@type': 'Question' as const,
                name: stripHtmlTags(safeString(question)),
                acceptedAnswer: {
                    '@type': 'Answer' as const,
                    text: stripHtmlTags(safeString(answer)),
                },
            };

            this.faq.mainEntity = this.faq.mainEntity || [];
            this.faq.mainEntity.push(questionEntity);
        }
        return this;
    }

    /**
     * Adds multiple questions to the FAQ, sorted by order property.
     *
     * @param questions - Array of FAQ items to add
     * @returns This builder instance for chaining
     */
    addQuestions(questions: FAQItem[]): FAQBuilder {
        const sortedQuestions = [...questions].sort((a, b) => {
            if (a.order !== undefined && b.order !== undefined) {
                return a.order - b.order;
            }
            if (a.order !== undefined) return -1;
            if (b.order !== undefined) return 1;
            return 0;
        });

        sortedQuestions.forEach((item) => {
            this.addQuestion(item.question, item.answer, item.category);
        });

        return this;
    }

    /**
     * Builds and returns the complete FAQ schema object.
     *
     * @returns The constructed FAQ schema
     * @throws Error if required properties are missing
     */
    build(): FAQResult {
        if (!this.faq.name) {
            throw new Error('FAQ must have a title');
        }

        if (!this.faq.mainEntity || this.faq.mainEntity.length === 0) {
            throw new Error('FAQ must have at least one question');
        }

        return this.faq as FAQResult;
    }
}

/**
 * Generates FAQ page schema markup from input data.
 *
 * @param input - FAQ input data containing title, questions, and optional metadata
 * @param input.title - The main title for the FAQ page
 * @param input.description - Optional description for the FAQ page
 * @param input.questions - Array of question/answer pairs
 * @param input.url - Optional URL for the FAQ page
 * @param input.lastModified - Optional last modified date
 * @param input.publisher - Optional publisher organization name
 * @param input.inLanguage - Optional language code
 * @param options - Optional configuration for generation behavior
 * @param options.useCache - Whether to use caching (default: true)
 * @param options.validateInput - Whether to validate input (default: true)
 * @param options.organizationName - Fallback organization name
 * @returns Result containing FAQPage schema or error
 *
 * @example
 * ```typescript
 * const faqSchema = generateFAQSchema({
 *   title: "Frequently Asked Questions",
 *   description: "Common questions about our service",
 *   questions: [
 *     {
 *       question: "What is your return policy?",
 *       answer: "We offer 30-day returns on all items.",
 *       order: 1
 *     }
 *   ]
 * });
 * ```
 */
export function generateFAQSchema(input: FAQInput, options: GeneratorOptions = {}): Result<FAQResult> {
    const { useCache = true, validateInput = true } = options;

    const cacheKey = useCache ? JSON.stringify(input) : '';

    if (useCache && cacheKey) {
        const cached = faqCache.get(cacheKey);
        if (cached) {
            logger.debug('FAQ schema cache hit', { input });
            return ok(cached);
        }
    }

    if (validateInput) {
        const validation = validateFAQInput(input);
        if (!isOk(validation)) {
            logger.warn('Invalid FAQ input', { errors: validation.error });
            return fail(new Error(`Invalid FAQ input: ${validation.error.map((e) => e.message).join(', ')}`));
        }
    }

    try {
        const builder = new FAQBuilder(input.title);

        const faq = builder
            .setDescription(input.description)
            .setUrl(input.url)
            .setLastModified(input.lastModified)
            .setPublisher(input.publisher || options.organizationName)
            .setLanguage(input.inLanguage)
            .addQuestions(input.questions)
            .build();

        if (useCache && cacheKey) {
            faqCache.set(cacheKey, faq);
        }

        logger.info('Generated FAQ schema', {
            title: input.title,
            questionCount: input.questions.length,
            hasDescription: !!input.description,
            hasUrl: !!input.url,
            cached: useCache,
        });

        return ok(faq);
    } catch (error) {
        logger.error('Failed to generate FAQ schema', { error, input });
        return fail(error instanceof Error ? error : new Error('Unknown error generating FAQ schema'));
    }
}
